/* eslint-disable react/prefer-stateless-function */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { fromJS } from 'immutable';
import injectReducer from 'utils/injectReducer';
import WizardNavigationButtons from '../components/WizardNavigationButtons';
import FNOLTooltip from '../../../components/FNOLTooltip';
import {
  ErrorMessage,
  Validate,
  ValidateGroup,
} from '../../../components/Validation';
import { required } from '../../../utils/validation/validator';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import { makeSelectFormHasChanges, makeSelectInsuredsList } from '../../App/selectors';
import { setFormHasChanges } from '../../App/actions';
import {
  saveFormData,
  handleFormDataChange,
  nextSlide,
  resetFormData,
  resetFormFields,
} from './actions';
import { resetFormData as resetTIFormData } from '../Tugo.TripInterruption/actions';
import reducer, { HEALTH_SUB_QUESTION_PREFIXES, PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES, EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES, LEGAL_ISSUES_SUB_QUESTION_PREFIXES, CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES, OTHER_REASONS_SUB_QUESTION_PREFIXES } from './reducer';
import messages from './messages';
import { makeSelectStepTripCancellationTravellerTugo, makeSelectStepTripCancellationTravellerTugoErrors } from './selectors';
import { AdditionalList } from './components/Additional';
import TripInterruptionContainer from '../Tugo.TripInterruption';

// Health sub-question definitions
const HEALTH_SUB_QUESTIONS = [
  {
    id: 'healthYouOrTravellingCompanion',
    key: 'youOrTravellingCompanion',
    message: messages.healthRelatedToYouOrTravellingCompanion,
  },
  {
    id: 'healthFamilyMember',
    key: 'familyMember',
    message: messages.healthRelatedToFamilyMember,
  },
  {
    id: 'healthBusinessPartnerOrEmployer',
    key: 'businessPartnerOrEmployer',
    message: messages.healthRelatedToBusinessPartnerOrEmployer,
  },
  {
    id: 'healthHostAtDestination',
    key: 'hostAtDestination',
    message: messages.healthRelatedToHostAtDestination,
  },
];

// Expense category definitions for each health sub-question
const EXPENSE_CATEGORIES = [
  {
    id: 'nonRefundablePrePaidTravelCosts',
    key: 'nonRefundablePrePaidTravelCosts',
    itemsField: 'NonRefundablePrePaidTravelCostsItems',
    message: messages.nonRefundablePrePaidTravelCosts,
    tooltip: null,
  },
  {
    id: 'nonRefundablePrePaidTransportationCosts',
    key: 'nonRefundablePrePaidTransportationCosts',
    itemsField: 'NonRefundablePrePaidTransportationCostsItems',
    message: messages.nonRefundablePrePaidTransportationCosts,
    tooltip: null,
  },
  {
    id: 'commercialAccommodationToResumeTravel',
    key: 'commercialAccommodationToResumeTravel',
    itemsField: 'CommercialAccommodationToResumeTravelItems',
    message: messages.commercialAccommodationToResumeTravel,
    tooltip: {
      dataFor: 'tooltip-commercial-accommodation',
      messageKey: 'tooltipCommercialAccommodation',
    },
  },
  {
    id: 'changeFeesIfCancellingPortionOfTrip',
    key: 'changeFeesIfCancellingPortionOfTrip',
    itemsField: 'ChangeFeesIfCancellingPortionOfTripItems',
    message: messages.changeFeesIfCancellingPortionOfTrip,
    isChangeFee: true,
    tooltip: null,
  },
  {
    id: 'singleSupplement',
    key: 'singleSupplement',
    itemsField: 'SingleSupplementItems',
    message: messages.singleSupplement,
    tooltip: null,
  },
];

// Pregnancy/adoption sub-question definitions
const PREGNANCY_ADOPTION_SUB_QUESTIONS = [
  {
    id: 'pregnancyLegalAdoption',
    key: 'legalAdoption',
    message: messages.pregnancyAdoptionLegalAdoption,
  },
  {
    id: 'pregnancyPregnancyDiagnosed',
    key: 'pregnancyDiagnosed',
    message: messages.pregnancyAdoptionPregnancyDiagnosed,
  },
  {
    id: 'pregnancyComplicationsOfPregnancy',
    key: 'complicationsOfPregnancy',
    message: messages.pregnancyAdoptionComplicationsOfPregnancy,
  },
];

// Employment/education sub-question definitions
const EMPLOYMENT_EDUCATION_SUB_QUESTIONS = [
  {
    id: 'employmentJobTransfer',
    key: 'jobTransfer',
    message: messages.employmentEducationJobTransfer,
  },
  {
    id: 'employmentInvoluntaryLossOfEmployment',
    key: 'involuntaryLossOfEmployment',
    message: messages.employmentEducationInvoluntaryLossOfEmployment,
  },
  {
    id: 'employmentCancellationOfBusinessMeeting',
    key: 'cancellationOfBusinessMeeting',
    message: messages.employmentEducationCancellationOfBusinessMeeting,
  },
  {
    id: 'employmentCancellationOfConference',
    key: 'cancellationOfConference',
    message: messages.employmentEducationCancellationOfConference,
  },
];

// Legal issues sub-question definitions
const LEGAL_ISSUES_SUB_QUESTIONS = [
  {
    id: 'legalCanadianGovernmentAdvisory',
    key: 'canadianGovernmentAdvisory',
    message: messages.legalIssuesCanadianGovernmentAdvisory,
  },
  {
    id: 'legalJuryDutyCourtProceeding',
    key: 'juryDutyCourtProceeding',
    message: messages.legalIssuesJuryDutyCourtProceeding,
  },
  {
    id: 'legalSummonedToService',
    key: 'summonedToService',
    message: messages.legalIssuesSummonedToService,
  },
  {
    id: 'legalOfficialTravelAdvisory',
    key: 'officialTravelAdvisory',
    message: messages.legalIssuesOfficialTravelAdvisory,
  },
  {
    id: 'legalNonIssuanceOfVisa',
    key: 'nonIssuanceOfVisa',
    message: messages.legalIssuesNonIssuanceOfVisa,
  },
];

// Tour-specific expense categories (only for tour operator cancellation sub-question)
const TOUR_EXPENSE_CATEGORIES = [
  {
    id: 'nonRefundablePrePaidTourCosts',
    key: 'nonRefundablePrePaidTourCosts',
    itemsField: 'NonRefundablePrePaidTourCostsItems',
    message: messages.nonRefundablePrePaidTourCosts,
    tooltip: null,
  },
  {
    id: 'nonRefundablePrePaidTransportationCostsNotPartOfTour',
    key: 'nonRefundablePrePaidTransportationCostsNotPartOfTour',
    itemsField: 'NonRefundablePrePaidTransportationCostsNotPartOfTourItems',
    message: messages.nonRefundablePrePaidTransportationCostsNotPartOfTour,
    tooltip: null,
  },
];

// Carrier/accident/weather sub-question definitions
const CARRIER_ACCIDENT_WEATHER_SUB_QUESTIONS = [
  {
    id: 'carrierMissedConnection',
    key: 'missedConnection',
    message: messages.carrierMissedConnection,
  },
  {
    id: 'carrierFourHoursNoConnection',
    key: 'fourHoursNoConnection',
    message: messages.carrierFourHoursNoConnection,
  },
  {
    id: 'carrierCancellationOfCommonCarrier',
    key: 'cancellationOfCommonCarrier',
    message: messages.carrierCancellationOfCommonCarrier,
  },
  {
    id: 'carrierTourOperatorCancellation',
    key: 'tourOperatorCancellation',
    message: messages.carrierTourOperatorCancellation,
  },
  {
    id: 'carrierAccidentOnWay',
    key: 'accidentOnWay',
    message: messages.carrierAccidentOnWay,
  },
  {
    id: 'carrierDelayOfPrivateVehicle',
    key: 'delayOfPrivateVehicle',
    message: messages.carrierDelayOfPrivateVehicle,
  },
];

// Other reasons sub-question definitions
const OTHER_REASONS_SUB_QUESTIONS = [
  {
    id: 'otherNaturalDisaster',
    key: 'naturalDisaster',
    message: messages.otherReasonsNaturalDisaster,
  },
  {
    id: 'otherUnforeseeableEvent',
    key: 'unforeseeableEvent',
    message: messages.otherReasonsUnforeseeableEvent,
  },
];

export class StepTripCancellationTravellerTugo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validationCounter: 0,
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.handleNext();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.validatorGroup.checkValidation();
    }
  }

  onChange = (field) => (event) => {
    const { value } = event.target;
    const propertyFullName = `form.${field}`;
    this.props.onFormDataChange(propertyFullName, value);
    this.props.setFormHasChanges(true);
  };

  // Convert Immutable list to JS array
  toJSArray = (immutableOrArray) => {
    if (!immutableOrArray) return [];
    if (typeof immutableOrArray.toJS === 'function') return immutableOrArray.toJS();
    return immutableOrArray;
  };

  // Ensure all items have IDs
  appendIds = (items) => {
    const list = this.toJSArray(items);
    return list.map((item) => {
      if (!item.id) {
        item.id = new Date().getTime().toString();
      }
      return item;
    });
  };

  // Handle expense items array change
  handleExpenseItemsChange = (formField) => (data) => {
    this.props.onFormDataChange(`form.${formField}`, fromJS(data));
    this.props.setFormHasChanges(true);
  };

  // Handle checkbox list toggle (add/remove value from array)
  handleCheckboxListToggle = (formField, currentList) => (value) => (event) => {
    const listChecked = [...currentList];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(value) === -1) {
        listChecked.push(value);
      }
    } else {
      const idx = listChecked.indexOf(value);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
    }
    this.props.onFormDataChange(`form.${formField}`, listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle main reason checkbox change with reset logic
  handleReasonCheckboxChange = (expensesSelected) => (value) => (event) => {
    const listChecked = [...expensesSelected];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(value) === -1) {
        listChecked.push(value);
      }
    } else {
      const idx = listChecked.indexOf(value);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset health sub-questions when health reason is unchecked
      if (value === 'reasonHealth') {
        this.resetAllHealthData();
      }
      // Reset pregnancy/adoption sub-questions when pregnancy reason is unchecked
      if (value === 'reasonPregnancyAdoption') {
        this.resetAllPregnancyAdoptionData();
      }
      // Reset employment/education sub-questions when employment reason is unchecked
      if (value === 'reasonEmploymentEducation') {
        this.resetAllEmploymentEducationData();
      }
      // Reset legal issues sub-questions when legal issues reason is unchecked
      if (value === 'reasonLegalIssues') {
        this.resetAllLegalIssuesData();
      }
      // Reset carrier/accident/weather sub-questions when carrier reason is unchecked
      if (value === 'reasonCarrierAccidentWeather') {
        this.resetAllCarrierAccidentWeatherData();
      }
      // Reset other reasons sub-questions when other reason is unchecked
      if (value === 'reasonOther') {
        this.resetAllOtherReasonsData();
      }
    }
    this.props.onFormDataChange('form.whatExpensesAreYouClaimingFor', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle health sub-question checkbox change with reset logic
  handleHealthSubQuestionToggle = (healthSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...healthSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
    }
    this.props.onFormDataChange('form.healthSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle pregnancy/adoption sub-question checkbox change with reset logic
  handlePregnancyAdoptionSubQuestionToggle = (pregnancyAdoptionSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...pregnancyAdoptionSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
    }
    this.props.onFormDataChange('form.pregnancyAdoptionSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle employment/education sub-question checkbox change with reset logic
  handleEmploymentEducationSubQuestionToggle = (employmentEducationSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...employmentEducationSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
    }
    this.props.onFormDataChange('form.employmentEducationSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle legal issues sub-question checkbox change with reset logic
  handleLegalIssuesSubQuestionToggle = (legalIssuesSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...legalIssuesSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
    }
    this.props.onFormDataChange('form.legalIssuesSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle carrier/accident/weather sub-question checkbox change with reset logic
  handleCarrierAccidentWeatherSubQuestionToggle = (carrierAccidentWeatherSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...carrierAccidentWeatherSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
      // Also reset tour-specific items if unchecking tourOperatorCancellation
      if (subQuestionKey === 'tourOperatorCancellation') {
        this.resetTourSubQuestionData(subQuestionKey);
      }
    }
    this.props.onFormDataChange('form.carrierAccidentWeatherSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle other reasons sub-question checkbox change with reset logic
  handleOtherReasonsSubQuestionToggle = (otherReasonsSubQuestions) => (subQuestionKey) => (event) => {
    const listChecked = [...otherReasonsSubQuestions];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(subQuestionKey) === -1) {
        listChecked.push(subQuestionKey);
      }
    } else {
      const idx = listChecked.indexOf(subQuestionKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset this sub-question's expense data
      this.resetHealthSubQuestionData(subQuestionKey);
    }
    this.props.onFormDataChange('form.otherReasonsSubQuestions', listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle expense category checkbox change with reset logic
  handleExpenseCategoryToggle = (prefix, selectedExpenses, categories = EXPENSE_CATEGORIES) => (categoryKey) => (event) => {
    const listChecked = [...selectedExpenses];
    const checked = event.target.checked;
    if (checked) {
      if (listChecked.indexOf(categoryKey) === -1) {
        listChecked.push(categoryKey);
      }
    } else {
      const idx = listChecked.indexOf(categoryKey);
      if (idx > -1) {
        listChecked.splice(idx, 1);
      }
      // Reset expense items for this category
      const category = categories.find((c) => c.key === categoryKey);
      if (category) {
        this.props.onFormDataChange(`form.${prefix}${category.itemsField}`, []);
      }
      // Reset change fee radio and items if unchecking change fees
      if (categoryKey === 'changeFeesIfCancellingPortionOfTrip') {
        this.props.onFormDataChange(`form.${prefix}ChangeFeeOption`, '');
      }
    }
    this.props.onFormDataChange(`form.${prefix}SelectedExpenses`, listChecked);
    this.props.setFormHasChanges(true);
  };

  // Handle change fee radio button selection
  handleChangeFeeRadioChange = (prefix) => (event) => {
    const { value } = event.target;
    this.props.onFormDataChange(`form.${prefix}ChangeFeeOption`, value);
    this.props.setFormHasChanges(true);
  };

  // Reset all health sub-question data
  resetAllHealthData = () => {
    this.props.onFormDataChange('form.healthSubQuestions', []);
    HEALTH_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
  };

  // Reset all pregnancy/adoption sub-question data
  resetAllPregnancyAdoptionData = () => {
    this.props.onFormDataChange('form.pregnancyAdoptionSubQuestions', []);
    PREGNANCY_ADOPTION_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
  };

  // Reset all employment/education sub-question data
  resetAllEmploymentEducationData = () => {
    this.props.onFormDataChange('form.employmentEducationSubQuestions', []);
    EMPLOYMENT_EDUCATION_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
  };

  // Reset all legal issues sub-question data
  resetAllLegalIssuesData = () => {
    this.props.onFormDataChange('form.legalIssuesSubQuestions', []);
    LEGAL_ISSUES_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
  };

  // Reset all carrier/accident/weather sub-question data
  resetAllCarrierAccidentWeatherData = () => {
    this.props.onFormDataChange('form.carrierAccidentWeatherSubQuestions', []);
    CARRIER_ACCIDENT_WEATHER_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
    // Also reset tour-specific items for tourOperatorCancellation
    this.resetTourSubQuestionData('tourOperatorCancellation');
  };

  // Reset all other reasons sub-question data
  resetAllOtherReasonsData = () => {
    this.props.onFormDataChange('form.otherReasonsSubQuestions', []);
    OTHER_REASONS_SUB_QUESTION_PREFIXES.forEach((prefix) => {
      this.resetHealthSubQuestionData(prefix);
    });
  };

  // Reset tour-specific expense item fields for a prefix
  resetTourSubQuestionData = (prefix) => {
    TOUR_EXPENSE_CATEGORIES.forEach((category) => {
      this.props.onFormDataChange(`form.${prefix}${category.itemsField}`, []);
    });
  };

  // Reset a single health sub-question's expense data
  resetHealthSubQuestionData = (prefix) => {
    this.props.onFormDataChange(`form.${prefix}SelectedExpenses`, []);
    this.props.onFormDataChange(`form.${prefix}ChangeFeeOption`, '');
    EXPENSE_CATEGORIES.forEach((category) => {
      this.props.onFormDataChange(`form.${prefix}${category.itemsField}`, []);
    });
  };

  // Reset all Trip Cancellation section data
  resetTripCancellationData = () => {
    this.props.onFormDataChange('form.whatExpensesAreYouClaimingFor', []);
    this.resetAllHealthData();
    this.resetAllPregnancyAdoptionData();
    this.resetAllEmploymentEducationData();
    this.resetAllLegalIssuesData();
    this.resetAllCarrierAccidentWeatherData();
    this.resetAllOtherReasonsData();
  };

  // Gateway: mutually exclusive journey change radio
  handleJourneyChangeSelection = (selection) => () => {
    if (selection === 'cancelledBeforeLeaving') {
      this.props.onResetTIData();
      this.props.onFormDataChange('form.changedAfterLeaving', '');
      this.props.onFormDataChange('form.cancelledBeforeLeaving', 'yes');
    } else if (selection === 'changedAfterLeaving') {
      this.resetTripCancellationData();
      this.props.onFormDataChange('form.cancelledBeforeLeaving', '');
      this.props.onFormDataChange('form.changedAfterLeaving', 'yes');
    }
    this.props.setFormHasChanges(true);
  };
  handleNext() {
    const { submitted, step } = this.props;

    if (submitted) {
      if (this.props.next) {
        this.props.next();
      }
      return;
    }

    const isValid = this.validatorGroup.checkValidation();

    this.props.onNext(!isValid);

    if (!isValid) {
      if (this.props.makeError) {
        this.props.makeError(step);
      }
      this.setState((prevState) => ({
        validationCounter: prevState.validationCounter + 1,
      }));
    } else {
      if (this.props.makeValid) {
        this.props.makeValid(step);
      }
      if (this.props.onSave) {
        this.props.onSave(this.props.form);
      }
      if (this.props.next) {
        this.props.next();
      }
    }
  }

  // Render expense items list for a given form field
  renderExpenseItems(formField) {
    const items = this.props.form[formField];
    if (!items) return null;

    const itemsArray = this.appendIds(items);
    const { submitted, insuredsList, intl } = this.props;
    const isReadOnly = submitted;

    return (
      <div className="ml-4 mt-2">
        <AdditionalList
          data={itemsArray}
          onChange={this.handleExpenseItemsChange(formField)}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
          insuredsList={insuredsList}
          intl={intl}
        />
      </div>
    );
  }

  // Render change fee radio buttons and expense box
  renderChangeFeeSection(prefix) {
    const { form, intl, submitted } = this.props;
    const changeFeeOption = form[`${prefix}ChangeFeeOption`] || '';

    return (
      <div className="ml-4 mt-2">
        {/* Radio: Change fees - portion of trip */}
        <div className="form-check mt-2">
          <input
            id={`${prefix}-changeFees-radio`}
            type="radio"
            className="form-check-input"
            name={`${prefix}ChangeFeeOption`}
            value="changeFeesPortionOfTrip"
            checked={changeFeeOption === 'changeFeesPortionOfTrip'}
            onChange={this.handleChangeFeeRadioChange(prefix)}
            disabled={submitted}
          />
          <label className="form-check-label" htmlFor={`${prefix}-changeFees-radio`}>
            <FormattedMessage {...messages.changeFeesPortionOfTrip} />
          </label>
          <FNOLTooltip
            dataFor={`${prefix}-tooltip-change-fees`}
            content={intl.formatMessage(messages.tooltipChangeFeesPortionOfTrip)}
          />
        </div>
        {changeFeeOption === 'changeFeesPortionOfTrip' &&
          this.renderExpenseItems(`${prefix}ChangeFeesIfCancellingPortionOfTripItems`)
        }

        {/* Radio: One way airfare - portion of trip */}
        <div className="form-check mt-2">
          <input
            id={`${prefix}-oneWayAirfare-radio`}
            type="radio"
            className="form-check-input"
            name={`${prefix}ChangeFeeOption`}
            value="oneWayAirfarePortionOfTrip"
            checked={changeFeeOption === 'oneWayAirfarePortionOfTrip'}
            onChange={this.handleChangeFeeRadioChange(prefix)}
            disabled={submitted}
          />
          <label className="form-check-label" htmlFor={`${prefix}-oneWayAirfare-radio`}>
            <FormattedMessage {...messages.oneWayAirfarePortionOfTrip} />
          </label>
          <FNOLTooltip
            dataFor={`${prefix}-tooltip-one-way-airfare`}
            content={intl.formatMessage(messages.tooltipOneWayAirfarePortionOfTrip)}
          />
        </div>
        {changeFeeOption === 'oneWayAirfarePortionOfTrip' &&
          this.renderExpenseItems(`${prefix}ChangeFeesIfCancellingPortionOfTripItems`)
        }
      </div>
    );
  }

  // Render expense categories for a health sub-question
  renderExpenseCategories(prefix, categories = EXPENSE_CATEGORIES) {
    const { form, intl, submitted } = this.props;
    const selectedExpenses = this.toJSArray(form[`${prefix}SelectedExpenses`]);
    const isExpenseCategoryChecked = (key) => selectedExpenses.indexOf(key) > -1;

    return (
      <div className="ml-4 mt-2">
        {categories.map((category) => (
          <div key={`${prefix}-${category.key}`}>
            <div className="form-check mt-2">
              <input
                id={`${prefix}-${category.id}`}
                type="checkbox"
                className="form-check-input"
                name={`${prefix}-${category.key}`}
                checked={isExpenseCategoryChecked(category.key)}
                onChange={this.handleExpenseCategoryToggle(prefix, selectedExpenses, categories)(category.key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={`${prefix}-${category.id}`}>
                <FormattedMessage {...category.message} />
                {category.isChangeFee && (
                  <span className="text-muted">
                    <FormattedMessage {...messages.changeFeesSelectOnlyOne} />
                  </span>
                )}
              </label>
              {category.tooltip && (
                <FNOLTooltip
                  dataFor={`${prefix}-${category.tooltip.dataFor}`}
                  content={intl.formatMessage(messages[category.tooltip.messageKey])}
                />
              )}
            </div>
            {/* Show expense box or change fee radios when category is checked */}
            {isExpenseCategoryChecked(category.key) && (
              category.isChangeFee
                ? this.renderChangeFeeSection(prefix)
                : this.renderExpenseItems(`${prefix}${category.itemsField}`)
            )}
          </div>
        ))}
      </div>
    );
  }

  // Render health sub-questions (shown when reasonHealth is checked)
  renderHealthSubQuestions() {
    const { form, submitted } = this.props;
    const healthSubQuestions = this.toJSArray(form.healthSubQuestions);
    const isSubQuestionChecked = (key) => healthSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {HEALTH_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handleHealthSubQuestionToggle(healthSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && this.renderExpenseCategories(key)}
          </div>
        ))}
      </div>
    );
  }

  // Render pregnancy/adoption sub-questions (shown when reasonPregnancyAdoption is checked)
  renderPregnancyAdoptionSubQuestions() {
    const { form, submitted } = this.props;
    const pregnancyAdoptionSubQuestions = this.toJSArray(form.pregnancyAdoptionSubQuestions);
    const isSubQuestionChecked = (key) => pregnancyAdoptionSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {PREGNANCY_ADOPTION_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handlePregnancyAdoptionSubQuestionToggle(pregnancyAdoptionSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && this.renderExpenseCategories(key)}
          </div>
        ))}
      </div>
    );
  }

  // Render employment/education sub-questions (shown when reasonEmploymentEducation is checked)
  renderEmploymentEducationSubQuestions() {
    const { form, submitted } = this.props;
    const employmentEducationSubQuestions = this.toJSArray(form.employmentEducationSubQuestions);
    const isSubQuestionChecked = (key) => employmentEducationSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {EMPLOYMENT_EDUCATION_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handleEmploymentEducationSubQuestionToggle(employmentEducationSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && this.renderExpenseCategories(key)}
          </div>
        ))}
      </div>
    );
  }

  // Render legal issues sub-questions (shown when reasonLegalIssues is checked)
  renderLegalIssuesSubQuestions() {
    const { form, submitted } = this.props;
    const legalIssuesSubQuestions = this.toJSArray(form.legalIssuesSubQuestions);
    const isSubQuestionChecked = (key) => legalIssuesSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {LEGAL_ISSUES_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handleLegalIssuesSubQuestionToggle(legalIssuesSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && this.renderExpenseCategories(key)}
          </div>
        ))}
      </div>
    );
  }

  // Render carrier/accident/weather sub-questions (shown when reasonCarrierAccidentWeather is checked)
  renderCarrierAccidentWeatherSubQuestions() {
    const { form, submitted } = this.props;
    const carrierAccidentWeatherSubQuestions = this.toJSArray(form.carrierAccidentWeatherSubQuestions);
    const isSubQuestionChecked = (key) => carrierAccidentWeatherSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {CARRIER_ACCIDENT_WEATHER_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handleCarrierAccidentWeatherSubQuestionToggle(carrierAccidentWeatherSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && (
              key === 'tourOperatorCancellation'
                ? this.renderExpenseCategories(key, TOUR_EXPENSE_CATEGORIES)
                : this.renderExpenseCategories(key)
            )}
          </div>
        ))}
      </div>
    );
  }

  // Render other reasons sub-questions (shown when reasonOther is checked)
  renderOtherReasonsSubQuestions() {
    const { form, submitted } = this.props;
    const otherReasonsSubQuestions = this.toJSArray(form.otherReasonsSubQuestions);
    const isSubQuestionChecked = (key) => otherReasonsSubQuestions.indexOf(key) > -1;

    return (
      <div className="ml-5 mt-2">
        {OTHER_REASONS_SUB_QUESTIONS.map(({ id, key, message }) => (
          <div key={key}>
            <div className="d-flex align-items-start mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input mt-1 mr-2"
                style={{ minWidth: '20px' }}
                name={key}
                checked={isSubQuestionChecked(key)}
                onChange={this.handleOtherReasonsSubQuestionToggle(otherReasonsSubQuestions)(key)}
                disabled={submitted}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show expense categories when sub-question is checked */}
            {isSubQuestionChecked(key) && this.renderExpenseCategories(key)}
          </div>
        ))}
      </div>
    );
  }

  renderTripCancellationReasonCheckboxes(expensesSelected, handleExpensesSelected) {
    const isChecked = (value) => expensesSelected.indexOf(value) > -1;

    const reasons = [
      {
        id: 'tripCanReasonHealth',
        name: 'reasonHealth',
        message: messages.reasonHealth,
      },
      {
        id: 'tripCanReasonPregnancyAdoption',
        name: 'reasonPregnancyAdoption',
        message: messages.reasonPregnancyAdoption,
      },
      {
        id: 'tripCanReasonEmploymentEducation',
        name: 'reasonEmploymentEducation',
        message: messages.reasonEmploymentEducation,
      },
      {
        id: 'tripCanReasonLegalIssues',
        name: 'reasonLegalIssues',
        message: messages.reasonLegalIssues,
      },
      {
        id: 'tripCanReasonCarrierAccidentWeather',
        name: 'reasonCarrierAccidentWeather',
        message: messages.reasonCarrierAccidentWeather,
      },
      {
        id: 'tripCanReasonOther',
        name: 'reasonOther',
        message: messages.reasonOther,
      },
    ];

    return (
      <div>
        {reasons.map(({ id, name, message }) => (
          <div key={name}>
            <div className="form-check mt-2">
              <input
                id={id}
                type="checkbox"
                className="form-check-input"
                name={name}
                checked={isChecked(name)}
                onChange={handleExpensesSelected(name)}
              />
              <label className="form-check-label" htmlFor={id}>
                <FormattedMessage {...message} />
              </label>
            </div>
            {/* Show health sub-questions when health reason is checked */}
            {name === 'reasonHealth' && isChecked(name) && this.renderHealthSubQuestions()}
            {/* Show pregnancy/adoption sub-questions when pregnancy reason is checked */}
            {name === 'reasonPregnancyAdoption' && isChecked(name) && this.renderPregnancyAdoptionSubQuestions()}
            {/* Show employment/education sub-questions when employment reason is checked */}
            {name === 'reasonEmploymentEducation' && isChecked(name) && this.renderEmploymentEducationSubQuestions()}
            {/* Show legal issues sub-questions when legal issues reason is checked */}
            {name === 'reasonLegalIssues' && isChecked(name) && this.renderLegalIssuesSubQuestions()}
            {/* Show carrier/accident/weather sub-questions when carrier reason is checked */}
            {name === 'reasonCarrierAccidentWeather' && isChecked(name) && this.renderCarrierAccidentWeatherSubQuestions()}
            {/* Show other reasons sub-questions when other reason is checked */}
            {name === 'reasonOther' && isChecked(name) && this.renderOtherReasonsSubQuestions()}
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { form, loading, intl, submitted } = this.props;
    const isReadOnly = submitted;
    const cancelledBeforeLeaving = form.cancelledBeforeLeaving || '';
    const changedAfterLeaving = form.changedAfterLeaving || '';
    const expensesSelected = this.toJSArray(form.whatExpensesAreYouClaimingFor);
    const handleExpensesSelected = this.handleReasonCheckboxChange(expensesSelected);

    return (
      <div className="app-content">
        {/* Section intro */}
        <div className="col-sm-12 form-group">
          <p><FormattedMessage {...messages.sectionIntro} /></p>
        </div>

        {/* Gateway: mutually exclusive journey change question */}
        <div className="col-sm-12 form-group">
          <div className="form-check">
            <input id="journeyChange-cancelledBeforeLeaving" type="radio" className="form-check-input"
              name="journeyChangeType"
              checked={cancelledBeforeLeaving === 'yes'}
              onChange={this.handleJourneyChangeSelection('cancelledBeforeLeaving')}
              disabled={isReadOnly} />
            <label className="form-check-label font-weight-bold" htmlFor="journeyChange-cancelledBeforeLeaving">
              <FormattedMessage {...messages.cancelledBeforeLeaving} />
            </label>
          </div>

          {/* Trip Cancellation section shown when cancelledBeforeLeaving = yes */}
          {cancelledBeforeLeaving === 'yes' && (
            <div>
              <div className="form-group mt-3">
                <p className="font-weight-bold"><FormattedMessage {...messages.introHeading} /></p>
                <p><FormattedMessage {...messages.introBody} /></p>
              </div>
              <div className="form-group ml-2">
                <Validate
                  name="whatExpensesAreYouClaimingFor"
                  validators={[required]}
                  group={this.validatorGroup}
                  addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                  validChangeInGroup={this.validatorGroup.validChangeInGroup}
                  validationCounter={this.state.validationCounter}
                  validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                  defaultValue={expensesSelected}
                  fieldName={messages.expensesQuestion}
                >
                  {this.renderTripCancellationReasonCheckboxes(expensesSelected, handleExpensesSelected)}
                  <ErrorMessage for="required">
                    <FormattedMessage {...messages.isRequired} values={messages.expensesQuestion} />
                  </ErrorMessage>
                </Validate>
              </div>
            </div>
          )}

          <div className="form-check mt-2">
            <input id="journeyChange-changedAfterLeaving" type="radio" className="form-check-input"
              name="journeyChangeType"
              checked={changedAfterLeaving === 'yes'}
              onChange={this.handleJourneyChangeSelection('changedAfterLeaving')}
              disabled={isReadOnly} />
            <label className="form-check-label font-weight-bold" htmlFor="journeyChange-changedAfterLeaving">
              <FormattedMessage {...messages.changedAfterLeaving} />
            </label>
          </div>

          {/* Trip Interruption section shown when changedAfterLeaving = yes */}
          {changedAfterLeaving === 'yes' && (
            <div className="ti-embedded-section mt-3">
              {/* Hide TI nav buttons TC owns navigation */}
              <style>{".ti-embedded-section .app-content > .row.justify-content-between { display: none !important; }"}</style>
              <TripInterruptionContainer />
            </div>
          )}
        </div>

        <WizardNavigationButtons
          onBack={this.props.prev}
          onNext={this.handleNext}
          onSave={() => this.props.onSave(form)}
          canMoveNext
          listLength={1}
          submitted={submitted}
          loading={loading}
          saveButtonId="tugo-trip-cancellation-save"
          showSpinnerForElement={() => false}
          handleKeyDown={() => { }}
          intl={intl}
          hasFormChanges={this.props.hasChanges}
          nextButtonRef={() => { }}
        />
      </div>
    );
  }
}

StepTripCancellationTravellerTugo.propTypes = {
  intl: intlShape.isRequired,
  form: PropTypes.object,
  errors: PropTypes.array,
  loading: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  setFormHasChanges: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  currentStep: PropTypes.number,
  step: PropTypes.number,
  stepName: PropTypes.string,
  validateStep: PropTypes.string,
  insuredsList: PropTypes.any,
  submitted: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectStepTripCancellationTravellerTugo(),
  errors: makeSelectStepTripCancellationTravellerTugoErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
  hasChanges: makeSelectFormHasChanges(),
  insuredsList: makeSelectInsuredsList(),
});

export function mapDispatchToProps(dispatch) {
  return {
    onSave: (form) => dispatch(saveFormData(form)),
    onFormDataChange: (name, value) => dispatch(handleFormDataChange(name, value)),
    onNext: (errors) => dispatch(nextSlide(errors)),
    resetFormData: () => dispatch(resetFormData()),
    onResetFormCustom: (fields) => dispatch(resetFormFields(fields)),
    onResetTIData: () => dispatch(resetTIFormData()),
    setFormHasChanges: (hasChanges) => dispatch(setFormHasChanges(hasChanges)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'stepTripCancellationTravellerTugo', reducer });

export default compose(
  withReducer,
  withConnect,
  injectIntl,
)(StepTripCancellationTravellerTugo);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Tugo.TripCancellationTraveller/index.js